import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './features/auth/components/Login';
import { Sentry } from './core/config/sentry';
import { AuthProvider, useAuth } from './core/context/AuthContext';
import { ThemeContextProvider } from './core/context/ThemeContext';
import { startVersionMonitoring, initializeVersion } from './shared/utils/versionControl';
import VersionModal from './shared/components/VersionModal';
import './styles/darkMode.css';
import GrillaDatosPracticas from './features/practicas/components/GrillaDatosPracticas';
import GrillaTurnos from './features/turnos/components/GrillaTurnos';
import Calendario from './features/calendario/components';
import { GrillaFacturacion } from './features/facturacion/components/GrillaFacturacion';
import FacturacionTecnicos from './features/facturacion/components/FacturacionTecnicos';
import GrillaRoles from './features/roles/components/GrillaRoles';
import ContabilidadPage from './features/contabilidad/components/ContabilidadPage';
import CargaFacturasOSPage from './features/contabilidad/components/CargaFacturasOSPage';
import GrillaRutasObrasSocial from './features/rutasObrasSocial/components/GrillaRutasObrasSocial';
import GrillaTokens from './features/tokens/components/GrillaTokens';
import GrillaTrazabilidad from './features/trazabilidad/components/GrillaTrazabilidad';
import GrillaCategorias from './features/categorias/components/GrillaCategorias';
import GrillaLocalidades from './features/localidades/components/GrillaLocalidades';
import GrillaMedicos from './features/medicos/components/GrillaMedicos';
import GrillaTecnicos from './features/tecnicos/components/GrillaTecnicos';
import GrillaTurnosCreados from './features/turnosCreados/components/GrillaTurnosCreados';
import GrillaObraSocialRequisitos from './features/obraSocialRequisitos/components/GrillaObraSocialRequisitos';
import GrillaRequisitos from './features/requisitos/components/GrillaRequisitos';
import GrillaTiposEstudios from './features/tiposEstudios/components/GrillaTiposEstudios';
import GrillaTiposEstudiosExt from './features/tiposEstudiosExt/components/GrillaTiposEstudiosExt';
import GrillaPagoPorEstudio from './features/pagoPorEstudio/components/GrillaPagoPorEstudio';
import GrillaImportesPersonal from './features/importesPersonal/components/GrillaImportesPersonal';
import GrillaUsuarios from './features/usuarios/components/GrillaUsuarios';
import GrillaColoresEstudios from './features/coloresEstudios/components/GrillaColoresEstudios';
import GrillaCms from './features/cms/components/GrillaCms';
import GrillaCentros from './features/centros/components/GrillaCentros';
import GrillaDerivadores from './features/derivadores/components/GrillaDerivadores';
import GrillaEquipos from './features/equipos/components/GrillaEquipos';
import GrillaObrasSociales from './features/obrasSociales/components/GrillaObrasSociales';
import GrillaConvenios from './features/convenios/components/GrillaConvenios';
import GrillaPacientes from './features/pacientes/components/GrillaPacientes';
import GrillaResumen from './features/resumen/components/GrillaResumen';
import GrillaEstudiosMedicos from './features/estudiosMedicos/components/GrillaEstudiosMedicos';
import GrillaHorariosMedicos from './features/horariosMedicos/components/GrillaHorariosMedicos';
import GrillaNotificacionesWP from './features/notificacionesWP/components/GrillaNotificacionesWP';
import GrillaEncuestas from './features/encuestas/components/GrillaEncuestas';
import VerificacionOmePage from './features/verificacionOme/pages/VerificacionOmePage';
import Welcome from './shared/components/Welcome';
import Header from './shared/components/layout/Header';
import Footer from './shared/components/layout/Footer';
import ChatWidget from './shared/components/ChatWidget';

function ProtectedRoute({ children, requiredMenu }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requiredMenu) {
    const menus = user?.menus || [];
    const hasAccess = Array.isArray(requiredMenu)
      ? requiredMenu.some(m => menus.includes(m))
      : menus.includes(requiredMenu);
    if (!hasAccess) return <Navigate to="/" />;
  }

  return <>{children}<ChatWidget /></>;
}

function App() {
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [forceLogout, setForceLogout] = useState(false);

  useEffect(() => {
    initializeVersion();
    const cleanup = startVersionMonitoring((shouldForceLogout) => {
      setForceLogout(shouldForceLogout);
      setShowVersionModal(true);
    });
    return cleanup;
  }, []);

  const handleVersionUpdate = async () => {
    if (forceLogout) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    } else {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-cache' });
        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('app_version', data.version);
        }
      } catch (error) {
        console.error('Error updating version:', error);
      }
      window.location.reload();
    }
  };

  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}><h2>Algo salió mal</h2><p>Por favor, recargá la página o contactá al administrador.</p><button onClick={() => window.location.reload()}>Recargar página</button></div>}>
      <ThemeContextProvider>
        <AuthProvider>
          <VersionModal show={showVersionModal} forceLogout={forceLogout} onConfirm={handleVersionUpdate} />
          <Router>
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<ProtectedRoute><><Header /><Welcome /><Footer /></></ProtectedRoute>} />

                <Route path="/grilla-datos-practicas" element={<ProtectedRoute requiredMenu={['Datos Practicas', 'Control de Informes']}><GrillaDatosPracticas /></ProtectedRoute>} />
                <Route path="/grilla-turnos" element={<ProtectedRoute requiredMenu={['Turnos', 'Estudios']}><GrillaTurnos /></ProtectedRoute>} />
                <Route path="/calendario" element={<ProtectedRoute requiredMenu="Calendario"><Calendario /></ProtectedRoute>} />
                <Route path="/turnos-creados" element={<ProtectedRoute requiredMenu="Turnos Creados"><GrillaTurnosCreados /></ProtectedRoute>} />
                <Route path="/estudios-medicos" element={<ProtectedRoute requiredMenu="Estudios Medicos"><GrillaEstudiosMedicos /></ProtectedRoute>} />
                <Route path="/resumen" element={<ProtectedRoute requiredMenu="Resumen"><GrillaResumen /></ProtectedRoute>} />
                <Route path="/historial-pacientes" element={<ProtectedRoute requiredMenu="Historial Pacientes"><GrillaPacientes /></ProtectedRoute>} />

                <Route path="/roles" element={<ProtectedRoute requiredMenu="Roles"><GrillaRoles /></ProtectedRoute>} />
                <Route path="/medicos" element={<ProtectedRoute requiredMenu="Medicos"><GrillaMedicos /></ProtectedRoute>} />
                <Route path="/tecnicos" element={<ProtectedRoute requiredMenu="Tecnicos"><GrillaTecnicos /></ProtectedRoute>} />
                <Route path="/horarios-medicos" element={<ProtectedRoute requiredMenu="Horarios"><GrillaHorariosMedicos /></ProtectedRoute>} />
                <Route path="/usuarios" element={<ProtectedRoute requiredMenu="Usuarios"><GrillaUsuarios /></ProtectedRoute>} />
                <Route path="/trazabilidad" element={<ProtectedRoute requiredMenu="Trazabilidad"><GrillaTrazabilidad /></ProtectedRoute>} />
                <Route path="/categorias" element={<ProtectedRoute requiredMenu="Categorias de practicas"><GrillaCategorias /></ProtectedRoute>} />
                <Route path="/tipos-estudios" element={<ProtectedRoute requiredMenu="Tipos de Practica"><GrillaTiposEstudios /></ProtectedRoute>} />
                <Route path="/tipos-estudios-ext" element={<ProtectedRoute requiredMenu="Tipos de Estudios Externos"><GrillaTiposEstudiosExt /></ProtectedRoute>} />
                <Route path="/pago-por-estudio" element={<ProtectedRoute requiredMenu="Pago por tipo de Practica"><GrillaPagoPorEstudio /></ProtectedRoute>} />
                <Route path="/administrar-importes" element={<ProtectedRoute requiredMenu={['Importe por Tecnico', 'Importe por Medico']}><GrillaImportesPersonal /></ProtectedRoute>} />
                <Route path="/colores-estudios" element={<ProtectedRoute requiredMenu="Colores de Tipos de Estudios"><GrillaColoresEstudios /></ProtectedRoute>} />
                <Route path="/cms" element={<ProtectedRoute requiredMenu="CMS Publicidad"><GrillaCms /></ProtectedRoute>} />
                <Route path="/centros" element={<ProtectedRoute requiredMenu="Centros"><GrillaCentros /></ProtectedRoute>} />
                <Route path="/localidades" element={<ProtectedRoute requiredMenu="Localidades"><GrillaLocalidades /></ProtectedRoute>} />
                <Route path="/derivadores" element={<ProtectedRoute requiredMenu="Derivador"><GrillaDerivadores /></ProtectedRoute>} />
                <Route path="/equipos" element={<ProtectedRoute requiredMenu="Equipos"><GrillaEquipos /></ProtectedRoute>} />
                <Route path="/obras-sociales" element={<ProtectedRoute requiredMenu="Obras Sociales"><GrillaObrasSociales /></ProtectedRoute>} />
                <Route path="/convenios" element={<ProtectedRoute requiredMenu="Convenio de Obra Sociales"><GrillaConvenios /></ProtectedRoute>} />
                <Route path="/requisitos" element={<ProtectedRoute requiredMenu="Requisitos"><GrillaRequisitos /></ProtectedRoute>} />
                <Route path="/obra-social-requisitos" element={<ProtectedRoute requiredMenu="Requisitos por Estudio"><GrillaObraSocialRequisitos /></ProtectedRoute>} />
                <Route path="/rutas-obra-social" element={<ProtectedRoute requiredMenu="Rutas dropbox"><GrillaRutasObrasSocial /></ProtectedRoute>} />
                <Route path="/tokens" element={<ProtectedRoute requiredMenu="Tokens"><GrillaTokens /></ProtectedRoute>} />

                <Route path="/grilla-facturacion" element={<ProtectedRoute requiredMenu="Facturacion OS"><GrillaFacturacion /></ProtectedRoute>} />
                <Route path="/facturacion-tecnicos" element={<ProtectedRoute requiredMenu={['Liquidacion Tecnicos', 'Liquidacion Medicos', 'Liquidacion Completa de Tecnicos', 'Liquidacion Completa de Medicos', 'Liquidacion']}><FacturacionTecnicos /></ProtectedRoute>} />

                <Route path="/contabilidad" element={<ProtectedRoute requiredMenu={['Contabilidad', 'Contabilidad General']}><ContabilidadPage /></ProtectedRoute>} />
                <Route path="/carga-facturas-os" element={<ProtectedRoute requiredMenu="Cargar Facturas"><CargaFacturasOSPage /></ProtectedRoute>} />
                <Route path="/notificaciones-wp" element={<ProtectedRoute requiredMenu="Notificaciones WP"><GrillaNotificacionesWP /></ProtectedRoute>} />
                <Route path="/encuestas" element={<ProtectedRoute requiredMenu="Encuestas"><GrillaEncuestas /></ProtectedRoute>} />
                <Route path="/verificacion-ome" element={<ProtectedRoute requiredMenu="Verificacion OME"><VerificacionOmePage /></ProtectedRoute>} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeContextProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
